// Page access defaults derived from ALL_LINKS roles (App.jsx)
// 'rw' = read+write, 'r' = read-only, false = no access

/**
 * @typedef {'rw' | 'r' | false} PageAccess
 */

/**
 * @typedef {'owner' | 'operator' | 'subcontractor' | 'client'} UserRole
 */

export const PAGE_ACCESS_DEFAULTS = /** @type {Record<string, Record<string, PageAccess>>} */ ({
  dashboard:   { owner: 'rw', operator: 'rw' },
  schedule:    { owner: 'rw', operator: 'rw' },
  clients:     { owner: 'rw', operator: 'rw' },
  crew:        { owner: 'rw', operator: 'rw' },
  compliance:  { owner: 'rw', operator: 'rw' },
  quotes:      { owner: 'rw', operator: 'rw' },
  leads:       { owner: 'rw', operator: 'rw' },
  invoices:    { owner: 'rw', operator: false },
  expenses:    { owner: 'rw', operator: false },
  contracts:   { owner: 'rw', operator: false },
  documents:   { owner: 'rw', operator: false },
  financials:  { owner: 'rw', operator: false },
  utilization: { owner: 'rw', operator: false },
  forms:       { owner: 'rw', operator: 'rw' },
  'field-work': { owner: 'rw', operator: 'rw' },
  settings:    { owner: 'rw', operator: false },
})

export const PAGE_LABELS = {
  dashboard:   'Dashboard',
  schedule:    'Schedule',
  clients:     'Clients',
  crew:        'Crew',
  compliance:  'Compliance',
  quotes:      'Quotes',
  leads:       'Leads',
  invoices:    'Invoices',
  expenses:    'Expenses',
  contracts:   'Contracts',
  documents:   'Documents',
  financials:  'Financials',
  utilization: 'Utilization',
  forms:       'Forms',
  'field-work': 'Field Work',
  approvals:   'Approvals',
  settings:    'Settings',
}

// Pages configurable per-operator (dashboard always accessible, settings always owner-only)
/** @type {Array<keyof typeof PAGE_LABELS>} */
export const CONFIGURABLE_PAGES = [
  'schedule', 'clients', 'crew', 'compliance', 'quotes', 'leads',
  'invoices', 'expenses', 'contracts', 'documents', 'financials', 'utilization', 'forms', 'field-work', 'approvals',
]

/**
 * Resolve effective access for a page given role and capabilities.
 * @param {string} pageKey
 * @param {string | null | undefined} role
 * @param {string[] | null | undefined} [capabilities]
 * @returns {PageAccess}
 */
export function resolvePageAccess(pageKey, role, capabilities) {
  if (role === 'owner') return 'rw'
  if (role === 'subcontractor') return false

  const caps = capabilities ?? []
  // Check explicit capability overrides
  for (const cap of caps) {
    if (cap === `-${pageKey}`) return false
    if (cap === `${pageKey}:rw`) return 'rw'
    if (cap === `${pageKey}:r`) return 'r'
  }

  // Fall back to role defaults
  /** @type {PageAccess | undefined} */
  const defaultAccess = role ? PAGE_ACCESS_DEFAULTS[pageKey]?.[role] : undefined
  return defaultAccess || false
}

/**
 * Whether a role can access a page at all (read or write).
 * @param {string} pageKey
 * @param {string | null | undefined} role
 * @param {string[] | null | undefined} [capabilities]
 * @returns {boolean}
 */
export function canAccessPage(pageKey, role, capabilities) {
  return resolvePageAccess(pageKey, role, capabilities) !== false
}

/**
 * Whether a role can write to a page.
 * @param {string} pageKey
 * @param {string | null | undefined} role
 * @param {string[] | null | undefined} [capabilities]
 * @returns {boolean}
 */
export function canWritePage(pageKey, role, capabilities) {
  return resolvePageAccess(pageKey, role, capabilities) === 'rw'
}
